import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus, IconUser, IconLayoutGrid, IconList, IconTrash, IconPhoto, IconSearch, IconX, IconPencil } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { notifications } from '@/lib/notifications';
import bulkPhotoService from '../../../services/bulkPhotoService';
import SelectField from '../../../components/common/SelectField';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';

const VIEW_KEY = 'bpe-view';
const POLL_MS = 2500;

export default function BulkPhotoEditList({ embedded = false }) {
    const navigate = useNavigate();
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const getCurrentUser = useAuthStore((s) => s.getCurrentUser);

    const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
    const [projects, setProjects] = useState([]);
    const [themeMap, setThemeMap] = useState({});
    const [loading, setLoading] = useState(true);
    const pollRef = useRef(null);

    // Filters (client-side over the loaded project list) + inline project rename.
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState('newest');
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');

    const changeView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };
    const fmtNum = (n) => (isBn ? String(n ?? 0).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]) : String(n ?? 0));

    const load = useCallback(async (silent = false) => {
        try {
            const res = await bulkPhotoService.list(
                selectedOfficeSpace ? { officeSpaceId: selectedOfficeSpace.id } : {}
            );
            setProjects(res.data || []);
        } catch (e) {
            if (!silent) notifications.show({ message: e.message || t('bulkPhotoEdit.loadError'), color: 'red' });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [t, selectedOfficeSpace]);

    useEffect(() => {
        bulkPhotoService.getThemes()
            .then((r) => setThemeMap(Object.fromEntries((r.data?.themes || []).map((th) => [th.id, th]))))
            .catch(() => {});
    }, []);

    // Reload (with a skeleton) whenever the active office space changes.
    useEffect(() => {
        setLoading(true);
        load();
    }, [load]);

    // Poll while any project is still processing.
    useEffect(() => {
        const anyProcessing = projects.some((p) => p.status === 'processing');
        clearInterval(pollRef.current);
        if (anyProcessing) pollRef.current = setInterval(() => load(true), POLL_MS);
        return () => clearInterval(pollRef.current);
    }, [projects, load]);

    // `theme` may be a CSV of "toolId" / "toolId:level" entries — strip the level,
    // localize each name and join. Falls back to the raw value / dash.
    const styleName = (id) => {
        const ids = String(id || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (!ids.length) return '—';
        const names = ids.map((entry) => { const tid = entry.split(':')[0]; const th = themeMap[tid]; return th ? (isBn ? th.nameBn || th.name : th.name) : tid; });
        return names.join(', ');
    };

    const statusBadge = (p) => {
        if (p.status === 'completed') return { label: t('bulkPhotoEdit.status.completed'), cls: 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30' };
        if (p.status === 'paused') return { label: t('bulkPhotoEdit.status.paused', { done: fmtNum(p.doneCount), total: fmtNum(p.photoCount) }), cls: 'text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/30' };
        return { label: t('bulkPhotoEdit.status.running', { done: fmtNum(p.doneCount), total: fmtNum(p.photoCount) }), cls: 'text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30' };
    };

    const metaText = (p) => `${t('bulkPhotoEdit.photoMeta', { count: fmtNum(p.photoCount) })} · ${styleName(p.theme)}`;
    const thumb = (img) => img?.enhancedUrl || img?.originalUrl || null;

    const progressBar = (p, className = '') => (
        <div className={`h-[5px] bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden ${className}`}>
            <div className={`h-full rounded-full transition-[width] duration-300 ${p.status === 'completed' ? 'bg-emerald-500' : 'bg-primary'}`} style={{ width: `${p.progress}%` }} />
        </div>
    );

    const handleDelete = async (e, p) => {
        e.stopPropagation();
        if (!window.confirm(t('bulkPhotoEdit.deleteConfirm'))) return;
        try {
            await bulkPhotoService.deleteProject(p.id);
            setProjects((prev) => prev.filter((x) => x.id !== p.id));
            getCurrentUser().catch(() => {}); // photos that had not finished are refunded
        } catch (err) {
            notifications.show({ message: err.message, color: 'red' });
        }
    };

    // ── Inline project rename ──────────────────────────────────────────────────
    const startRename = (e, p) => { e.stopPropagation(); setRenamingId(p.id); setRenameValue(p.name || ''); };
    const cancelRename = () => { setRenamingId(null); setRenameValue(''); };
    const saveRename = async (p) => {
        const name = renameValue.trim();
        if (!name || name === p.name) { cancelRename(); return; }
        try {
            await bulkPhotoService.renameProject(p.id, name);
            setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, name } : x)));
        } catch (err) {
            notifications.show({ message: err.message, color: 'red' });
        } finally { cancelRename(); }
    };
    const renderName = (p, className) => (
        renamingId === p.id ? (
            <input
                autoFocus
                value={renameValue}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveRename(p); if (e.key === 'Escape') cancelRename(); }}
                onBlur={() => saveRename(p)}
                className="min-w-0 flex-1 px-1.5 py-0.5 rounded border border-primary/60 bg-white dark:bg-zinc-800 text-[14px] text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
        ) : (
            <span className={className}>{p.name}</span>
        )
    );

    // Client-side search + status filter + sort over the loaded projects.
    const filteredProjects = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = projects.filter((p) => {
            if (q && !(p.name || '').toLowerCase().includes(q)) return false;
            if (statusFilter !== 'all' && p.status !== statusFilter) return false;
            return true;
        });
        return list.sort((a, b) => {
            const da = new Date(a.createdAt || 0).getTime();
            const db = new Date(b.createdAt || 0).getTime();
            return sortOrder === 'oldest' ? da - db : db - da;
        });
    }, [projects, search, statusFilter, sortOrder]);
    const hasActiveFilters = !!search.trim() || statusFilter !== 'all' || sortOrder !== 'newest';

    const Tile = ({ img, size = 42 }) => (
        <div className="absolute inset-0 bg-zinc-100 dark:bg-zinc-800">
            {thumb(img) ? (
                <img src={thumb(img)} alt="" className="w-full h-full object-contain" draggable="false" />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                    <IconUser size={size} stroke={1.1} className="text-zinc-300 dark:text-zinc-600" />
                </div>
            )}
        </div>
    );

    const VIEW_OPTIONS = [
        { key: 'grid', Icon: IconLayoutGrid, label: t('bulkPhotoEdit.viewGrid') },
        { key: 'list', Icon: IconList, label: t('bulkPhotoEdit.viewList') },
    ];

    return (
        <div className={`w-full font-hind space-y-6 ${embedded ? '' : 'py-2 sm:py-4'}`}>
            {/* Header (standalone page only) — title + New. In the My Files tab the
                surrounding tab already labels the section, so this is dropped and the
                view toggle lives in the toolbar row below. */}
            {!embedded && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{t('bulkPhotoEdit.title')}</h1>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1 max-w-2xl">{t('bulkPhotoEdit.subtitle')}</p>
                    </div>
                    <button onClick={() => navigate('/bulk-photo-edit/new')} className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl shadow-sm shadow-primary/30 transition-colors flex-shrink-0">
                        <IconPlus size={16} stroke={2.2} />{t('bulkPhotoEdit.newButton')}
                    </button>
                </div>
            )}

            {/* Toolbar — search + status + sort + reset + view toggle, all in ONE row */}
            {!loading && projects.length > 0 && (
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[200px] flex items-center gap-2.5 h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900">
                        <IconSearch size={18} className="text-zinc-400 flex-shrink-0" />
                        <input
                            type="text"
                            className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400"
                            placeholder={t('bulkPhotoEdit.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button type="button" onClick={() => setSearch('')} aria-label={t('common.clear') || 'Clear'}
                                className="flex-shrink-0 p-0.5 rounded-full text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors">
                                <IconX size={16} />
                            </button>
                        )}
                    </div>
                    <div className="w-full sm:w-[170px]">
                        <SelectField
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'all', label: t('bulkPhotoEdit.filterAllStatus') },
                                { value: 'completed', label: t('bulkPhotoEdit.status.completed') },
                                { value: 'processing', label: t('bulkPhotoEdit.filterProcessing') },
                                { value: 'paused', label: t('bulkPhotoEdit.filterPaused') },
                            ]}
                            triggerClassName="h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-zinc-800 dark:text-zinc-100"
                        />
                    </div>
                    <div className="w-full sm:w-[160px]">
                        <SelectField
                            value={sortOrder}
                            onChange={setSortOrder}
                            options={[
                                { value: 'newest', label: t('common.newest') || 'Newest first' },
                                { value: 'oldest', label: t('common.oldest') || 'Oldest first' },
                            ]}
                            triggerClassName="h-11 px-3.5 border border-zinc-200 dark:border-zinc-700 rounded-[11px] bg-white dark:bg-zinc-900 text-[13.5px] text-zinc-800 dark:text-zinc-100"
                        />
                    </div>
                    {hasActiveFilters && (
                        <button type="button" onClick={() => { setSearch(''); setStatusFilter('all'); setSortOrder('newest'); }}
                            className="h-11 px-3.5 inline-flex items-center gap-1.5 rounded-[11px] border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13.5px] font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex-shrink-0">
                            <IconX size={15} /> {t('common.reset')}
                        </button>
                    )}
                    {/* View toggle — same row as the filters (was previously its own row) */}
                    <div className="inline-flex gap-0.5 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5 flex-shrink-0 ml-auto">
                        {VIEW_OPTIONS.map(({ key, Icon, label }) => (
                            <button key={key} onClick={() => changeView(key)} title={label} aria-label={label} aria-pressed={view === key}
                                className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors ${view === key ? 'bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                                <Icon size={18} stroke={1.8} />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* No results (filtered) */}
            {!loading && projects.length > 0 && filteredProjects.length === 0 && (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {t('bulkPhotoEdit.noResults')}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-2.5 animate-pulse">
                            <div className="h-[158px] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
                            <div className="h-4 w-2/3 bg-zinc-100 dark:bg-zinc-800 rounded mt-3" />
                            <div className="h-3 w-1/2 bg-zinc-100 dark:bg-zinc-800 rounded mt-2" />
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!loading && projects.length === 0 && (
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-12 flex flex-col items-center text-center">
                    <span className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4"><IconPhoto size={26} stroke={1.6} /></span>
                    <p className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{t('bulkPhotoEdit.empty')}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1 max-w-sm">{t('bulkPhotoEdit.emptyHint')}</p>
                    <button onClick={() => navigate('/bulk-photo-edit/new')} className="mt-5 flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl">
                        <IconPlus size={16} stroke={2.2} />{t('bulkPhotoEdit.newButton')}
                    </button>
                </div>
            )}

            {/* Grid view */}
            {!loading && view === 'grid' && filteredProjects.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {filteredProjects.map((p) => {
                        const badge = statusBadge(p);
                        const imgs = (p.images || []).slice(0, 5);
                        const rest = imgs.slice(1);
                        return (
                            <div key={p.id} onClick={() => navigate(`/bulk-photo-edit/${p.id}`)}
                                className="group relative text-left bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-2.5 cursor-pointer transition-all hover:border-primary/40 hover:shadow-md">
                                <div className="absolute top-3.5 right-3.5 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={(e) => startRename(e, p)} title={t('common.rename') || 'Rename'}
                                        className="w-7 h-7 rounded-lg bg-white/90 dark:bg-zinc-800/90 border border-zinc-200 dark:border-zinc-700 text-zinc-400 hover:text-primary flex items-center justify-center">
                                        <IconPencil size={13} />
                                    </button>
                                    <button onClick={(e) => handleDelete(e, p)} title={t('bulkPhotoEdit.detail.deleteProject')}
                                        className="w-7 h-7 rounded-lg bg-white/90 dark:bg-zinc-800/90 border border-zinc-200 dark:border-zinc-700 text-zinc-400 hover:text-red-500 flex items-center justify-center">
                                        <IconTrash size={14} />
                                    </button>
                                </div>
                                <div className="flex gap-1.5 h-[158px]">
                                    <div className="flex-[1.5] rounded-xl overflow-hidden relative"><Tile img={imgs[0]} size={42} /></div>
                                    {rest.length > 0 && (
                                        <div className={`flex-1 grid gap-1.5 ${rest.length >= 3 ? 'grid-cols-2' : 'grid-cols-1'}`} style={{ gridAutoRows: '1fr' }}>
                                            {rest.map((im, k) => (
                                                <div key={k} className="rounded-xl overflow-hidden relative"><Tile img={im} size={20} /></div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="px-1.5 pt-3 pb-1">
                                    <div className="flex items-center justify-between gap-2.5">
                                        {renderName(p, 'text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 truncate')}
                                        <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${badge.cls}`}>{badge.label}</span>
                                    </div>
                                    <div className="text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-1.5 truncate">{metaText(p)}</div>
                                    {progressBar(p, 'mt-2.5')}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* List view */}
            {!loading && view === 'list' && filteredProjects.length > 0 && (
                <div className="flex flex-col gap-2.5">
                    {filteredProjects.map((p) => {
                        const badge = statusBadge(p);
                        return (
                            <div key={p.id} onClick={() => navigate(`/bulk-photo-edit/${p.id}`)}
                                className="group flex items-center gap-4 text-left bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl p-2.5 sm:p-3 cursor-pointer transition-all hover:border-primary/40 hover:shadow-sm">
                                <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0"><Tile img={(p.images || [])[0]} size={26} /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1">
                                        {renderName(p, 'block text-[14.5px] font-semibold text-zinc-900 dark:text-zinc-100 truncate')}
                                        {renamingId !== p.id && (
                                            <button onClick={(e) => startRename(e, p)} title={t('common.rename') || 'Rename'}
                                                className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-primary flex-shrink-0 p-0.5 transition-opacity">
                                                <IconPencil size={13} />
                                            </button>
                                        )}
                                    </div>
                                    <span className="block text-[12.5px] text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">{metaText(p)}</span>
                                </div>
                                <div className="hidden md:flex items-center gap-2 w-44 flex-shrink-0">
                                    {progressBar(p, 'flex-1')}
                                    <span className="text-[11px] font-medium text-zinc-400 dark:text-zinc-500 w-9 text-right">{fmtNum(p.progress)}%</span>
                                </div>
                                <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${badge.cls}`}>{badge.label}</span>
                                <button onClick={(e) => handleDelete(e, p)} title={t('bulkPhotoEdit.detail.deleteProject')}
                                    className="w-8 h-8 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <IconTrash size={15} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
